import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Modal, Spin } from 'antd'
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons'
import {
  useComplianceJobs,
  useCreateComplianceJobMutation,
  useDeleteComplianceJobMutation,
} from '@/features/compliance/hooks'
import { ComplianceJobStatus, type TrialBalancePlatform } from '@/features/compliance/types'
import { fyToPeriodEndDate } from '@/features/compliance/utils'
import { useToast } from '@/contexts/ToastContext'
import { ComplianceJobsTable, CreateJobModal } from './index'

const POLL_INTERVAL_MS = 3000

type OrgJobsExpandedPanelProps = {
  org: TrialBalancePlatform
  clientId: string
  isExpanded: boolean
}

export function OrgJobsExpandedPanel({ org, clientId, isExpanded }: OrgJobsExpandedPanelProps) {
  const navigate = useNavigate()
  const { addToast } = useToast()
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectedFy, setSelectedFy] = useState<string | null>(null)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 })

  const skip = ((pagination.current || 1) - 1) * (pagination.pageSize || 10)
  const take = pagination.pageSize || 10
  const orgJobKey = org.companyId || org.platformId

  const jobsQuery = useComplianceJobs(clientId, skip, take, orgJobKey, {
    enabled: isExpanded && !!clientId,
    refetchInterval: (query) =>
      isExpanded &&
      (query.state.data?.items ?? []).some(
        (j) =>
          j.status === ComplianceJobStatus.Pending ||
          j.status === ComplianceJobStatus.Processing ||
          j.status === ComplianceJobStatus.Reviewing
      )
        ? POLL_INTERVAL_MS
        : false,
  })
  const createMutation = useCreateComplianceJobMutation(clientId)
  const deleteMutation = useDeleteComplianceJobMutation(clientId)

  const jobs = jobsQuery.data?.items ?? []
  const total = jobsQuery.data?.total ?? 0

  const handleDelete = (jobId: string) => {
    Modal.confirm({
      title: 'Delete job',
      content: 'Are you sure you want to delete this compliance job?',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(jobId)
          addToast('Job deleted successfully', 'success')
          void jobsQuery.refetch()
        } catch (e) {
          addToast(e instanceof Error ? e.message : 'Failed to delete job', 'error')
        }
      },
    })
  }

  const handleCreateJob = async () => {
    if (!selectedFy) return
    const periodEndDate = fyToPeriodEndDate(selectedFy)
    try {
      const res = await createMutation.mutateAsync({
        periodEndDate,
        orgId: orgJobKey,
      })
      addToast('Job created successfully', 'success')
      setCreateModalOpen(false)
      setSelectedFy(null)
      void jobsQuery.refetch()
      navigate(`/annual-compliance/org/${org.platformId}/job/${res.jobId}`)
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Failed to create job', 'error')
    }
  }

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false)
    setSelectedFy(null)
  }

  return (
    <div className="feature-panel-soft-strong border-t">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-2">
        <p className="text-sm text-gray-600">
          Jobs for <span className="font-medium text-gray-900">{org.name}</span>
        </p>
        <div className="flex items-center gap-2">
          {jobsQuery.isFetching && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500" role="status" aria-live="polite">
              <Spin size="small" indicator={<LoadingOutlined spin />} />
              Updating...
            </span>
          )}
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
            Create new job
          </Button>
        </div>
      </div>

      {jobsQuery.isError && (
        <div className="px-4 pb-2">
          <Alert
            type="error"
            showIcon
            message={(jobsQuery.error as Error)?.message || 'Failed to load jobs'}
          />
        </div>
      )}

      <ComplianceJobsTable
        jobs={jobs}
        loading={jobsQuery.isLoading}
        pagination={pagination}
        total={total}
        onPaginationChange={(page, pageSize) => setPagination({ current: page, pageSize: pageSize || 10 })}
        onDelete={handleDelete}
        orgPlatformId={org.platformId}
      />

      <CreateJobModal
        open={createModalOpen}
        selectedFy={selectedFy}
        onSelectedFyChange={setSelectedFy}
        onCancel={handleCloseCreateModal}
        onConfirm={handleCreateJob}
        isLoading={createMutation.isPending}
      />
    </div>
  )
}
